import { Classification } from "@/components/Classification";
import { Heading } from "@/components/heading/Heading";
import { HeadingSection } from "@/components/heading/HeadingSection";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { DatePickerField } from "@/features/projects/cards/components/DatePickerField";
import { TextAreaField } from "@/features/projects/cards/components/TextAreaField";
import { type UpdateOpportunity, updateProject } from "@repo/api";
import { ListView, ListViewItem } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  FlexDiv,
  InputField,
  ModalSheet,
  NumberField,
  SectionContainer,
  translateServices,
  useServices,
  useSettings,
  useToast,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { formatISO } from "date-fns";
import isEmpty from "lodash/isEmpty";

interface NeedAnalysisModalProps {
  isOpen: boolean;
  closeModal: () => void;
  focusField?: string;
}

export const NeedAnalysisModal = ({
  isOpen,
  closeModal,
  focusField,
}: NeedAnalysisModalProps) => {
  const { updateToast } = useToast();
  const { pushModal } = useModals();
  const { opportunity } = useActiveProject();
  const servicesList = useServices();
  const { quotation } = useSettings();
  const update = (newOpportunity: UpdateOpportunity) => {
    return updateProject(newOpportunity, updateToast);
  };

  return (
    <ModalSheet
      title={t("label.need_analysis")}
      visible={isOpen}
      onClose={closeModal}
    >
      {opportunity && (
        <HeadingSection value={3}>
          <SectionContainer>
            <Heading textStyle="Heading.XS">{t("label.main")}</Heading>

            <SectionContainer>
              <InputField
                id="title"
                autoFocus={!focusField}
                editable={!opportunity.lockStatus}
                label={t("label.title")}
                value={opportunity.title}
                onChange={(newValue) => {
                  if (newValue !== "") {
                    update({
                      id: opportunity.id,
                      title: newValue,
                    });
                  }
                }}
              />
              <NumberField
                id="budget"
                label={t("label.budget")}
                autoFocus={focusField === "budget"}
                editable={!opportunity.lockStatus}
                value={opportunity.customerBudget}
                onChange={(newValue) => {
                  update({
                    id: opportunity.id,
                    customerBudget: isEmpty(newValue)
                      ? ""
                      : Number(newValue)?.toFixed(2),
                  });
                  trackAnalytics("change_budget");
                }}
                prefix={opportunity.currencySymbol}
              />

              <DatePickerField
                id="expectedPurchaseDate"
                editable={!opportunity.lockStatus}
                autoFocus={focusField === "expectedPurchaseDate"}
                label={t("label.expected_purchase_date")}
                value={
                  opportunity.expectedPurchase &&
                  opportunity.expectedPurchase !== ""
                    ? new Date(`${opportunity.expectedPurchase}T12:00:00`)
                    : undefined
                }
                onChange={(newValue) => {
                  const convertToUTCTimezone =
                    newValue &&
                    formatISO(
                      new Date(formatISO(newValue, { representation: "date" }))
                    );

                  update({
                    id: opportunity.id,
                    expectedPurchase: newValue ? convertToUTCTimezone : "",
                  });
                  trackAnalytics("expected_purchase_date");
                }}
                isClearable
              />
              <TextAreaField
                id="interestArea"
                editable={!opportunity.lockStatus}
                maxLength={2000}
                label={t("label.interest_area")}
                value={opportunity.interestArea}
                onChange={(newValue) => {
                  update({
                    id: opportunity.id,
                    interestArea: newValue,
                  });
                  trackAnalytics("interest_area");
                }}
              />
              <ListView id="need-analysis-list">
                {quotation?.enable_quotations && (
                  <ListViewItem
                    title={t("label.quotations")}
                    control={
                      !opportunity.lockStatus ? "navigational" : "default"
                    }
                    onClick={() => {
                      pushModal({
                        type: "QUOTATIONS",
                      });
                      trackAnalytics("open_add_quotations");
                    }}
                  />
                )}

                <ListViewItem
                  onClick={() => {
                    pushModal({
                      type: "CLASSIFICATION",
                      classification: opportunity.classification,
                    });
                    trackAnalytics("open_classification_modal");
                  }}
                  title={t("label.classification")}
                  description={
                    <FlexDiv>
                      <Classification
                        id={opportunity.id.toString()}
                        classification={opportunity.classification}
                        showLabel
                      />
                    </FlexDiv>
                  }
                  control={!opportunity.lockStatus ? "navigational" : "default"}
                />

                <ListViewItem
                  title={t("label.services_interested")}
                  description={opportunity.servicesInterested
                    ?.map((el) => translateServices(servicesList?.[el]))
                    .join(", ")}
                  control={!opportunity.lockStatus ? "navigational" : "default"}
                  onClick={() => {
                    pushModal({
                      type: "SERVICES_INTERESTED",
                      servicesInterested: opportunity.servicesInterested,
                      onSave: (services) => {
                        return update({
                          id: opportunity.id,
                          servicesInterested: services,
                        }).finally(() => {
                          trackAnalytics("save_selected_services_interested");
                        });
                      },
                    });
                    trackAnalytics("open_services_interested_modal");
                  }}
                />
              </ListView>
            </SectionContainer>
          </SectionContainer>
        </HeadingSection>
      )}
    </ModalSheet>
  );
};
